/**
 * First-run setup (1.0.0-rc.4): shown to an administrator before anything else while the installation still has
 * the example administrator password (or right after the first administrator was created): the administrator
 * account, the public URL, the storage tiers, and optionally email and single sign-on; it ends with the System
 * status checks. It can be skipped; it is never shown on an installation with a real administrator password.
 */
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Check, CircleAlert, Globe, HardDrive, KeyRound, Loader2, Mail, ShieldCheck, UserRound } from 'lucide-react';
import { api, errorMessage } from '../api/client';
import type { Me } from '../api/hooks';
import { queryKeys } from '../api/queryKeys';
import { toast } from '../components/Toasts';

interface Tier { name: string; kind: string; availability: string; lastError: string | null }
export interface SetupDTO {
  needed: boolean;
  reason: 'no_admin' | 'example_password' | null;
  completed?: unknown;
  account?: { email: string; displayName: string; examplePassword: boolean };
  publicUrl?: { configured: string; seen: string; matches: boolean; cookieSecure: boolean };
  storage?: Record<'originals' | 'derivatives' | 'collateral' | 'exports', Tier | null>;
  email?: { configured: boolean };
  sso?: { mode: string; providers: number };
}

export const useSetup = (enabled: boolean) => useQuery({ queryKey: ['setup'], queryFn: () => api<SetupDTO>('/api/v1/setup'), enabled, staleTime: 30_000 });

const STEPS = [
  { id: 'account', label: 'Administrator', icon: <UserRound size={15} /> },
  { id: 'url', label: 'Public URL', icon: <Globe size={15} /> },
  { id: 'storage', label: 'Storage', icon: <HardDrive size={15} /> },
  { id: 'email', label: 'Email (optional)', icon: <Mail size={15} /> },
  { id: 'sso', label: 'Single sign-on (optional)', icon: <ShieldCheck size={15} /> },
  { id: 'done', label: 'Checks', icon: <Check size={15} /> },
] as const;
type StepId = (typeof STEPS)[number]['id'];

function Ok({ ok, children }: { ok: boolean; children: ReactNode }) {
  return <div className={`setup-check ${ok ? 'ok' : 'warn'}`}>{ok ? <Check size={15} /> : <CircleAlert size={15} />}<span>{children}</span></div>;
}

export function SetupWizard({ me }: { me: Me }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useSetup(true);
  const { pathname } = useLocation();
  // The wizard lives at /setup while it runs, so it stays once its first step no longer makes setup "needed".
  useEffect(() => { if (pathname !== '/setup') navigate('/setup', { replace: true }); }, [pathname, navigate]);
  const [step, setStep] = useState<StepId>('account');
  const [busy, setBusy] = useState(false);
  const s = q.data;
  const finish = async (to: string, skipped = false) => {
    setBusy(true);
    try {
      await api('/api/v1/setup/complete', { method: 'POST', json: { skipped } });
      await qc.invalidateQueries({ queryKey: ['setup'] });
      await qc.invalidateQueries({ queryKey: ['auth-hints'] });
      navigate(to, { replace: true });
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };
  const index = STEPS.findIndex((x) => x.id === step);
  const next = () => setStep(STEPS[Math.min(STEPS.length - 1, index + 1)]!.id);
  return (
    <div className="setup-page" data-testid="setup-wizard">
      <aside className="setup-rail">
        <div className="brand"><img src="/prism.svg" width={26} height={26} alt="" /> PRISM</div>
        <h1>Set up PRISM</h1>
        <p className="muted">A few checks before your team starts. Nothing here is hard to change later.</p>
        <ol className="setup-steps">
          {STEPS.map((x, i) => (
            <li key={x.id} className={x.id === step ? 'current' : i < index ? 'done' : ''}>
              <button type="button" onClick={() => setStep(x.id)} data-testid={`setup-step-${x.id}`}>{i < index ? <Check size={15} /> : x.icon}{x.label}</button>
            </li>
          ))}
        </ol>
        <button type="button" className="btn ghost sm" disabled={busy} onClick={() => { if (window.confirm('Skip setup for now? System status keeps reminding you of anything left, such as the example password.')) void finish('/', true); }} data-testid="setup-skip">Skip setup for now</button>
      </aside>
      <main className="setup-main">
        {!s ? <div className="skeleton" style={{ height: 240 }} /> : (
          <>
            {step === 'account' && <AccountStep me={me} s={s} onDone={() => { void q.refetch(); next(); }} />}
            {step === 'url' && s.publicUrl && (
              <section className="setup-card">
                <h2>Public URL</h2>
                <p className="muted">The address people use to open PRISM, set as <code>PRISM_PUBLIC_URL</code> in <code>.env</code>. Links in email, single sign-on and the cross-site check use it.</p>
                <dl className="kv"><dt>Configured</dt><dd className="mono">{s.publicUrl.configured}</dd><dt>You opened</dt><dd className="mono">{s.publicUrl.seen}</dd></dl>
                <Ok ok={s.publicUrl.matches}>{s.publicUrl.matches ? 'They match.' : 'They differ. If people open PRISM at the second address, set PRISM_PUBLIC_URL to it in .env and restart (docker compose up -d).'}</Ok>
                <Ok ok={s.publicUrl.cookieSecure || !/^https:/.test(s.publicUrl.configured)}>{s.publicUrl.cookieSecure ? 'Sign-in cookies are sent over HTTPS only.' : 'The public URL is not https: fine on this machine, but use HTTPS (for example Caddy in front) for a shared installation.'}</Ok>
                <div className="setup-actions"><button className="btn primary" onClick={next} data-testid="setup-next">Next <ArrowRight size={14} /></button></div>
              </section>
            )}
            {step === 'storage' && s.storage && (
              <section className="setup-card">
                <h2>Storage tiers</h2>
                <p className="muted">Where PRISM keeps originals, the proxies and other derivatives it makes, attachments, and exports. Change them under Administration → Storage.</p>
                <table className="table compact" data-testid="setup-storage">
                  <thead><tr><th>Tier</th><th>Location</th><th>State</th></tr></thead>
                  <tbody>{(['originals', 'derivatives', 'collateral', 'exports'] as const).map((t) => {
                    const l = s.storage![t];
                    return <tr key={t}><td>{t[0]!.toUpperCase() + t.slice(1)}</td><td>{l ? `${l.name} (${l.kind})` : <span className="faint">Not set</span>}</td>
                      <td>{l ? <span className={`pill ${l.availability === 'online' ? 'accent' : 'warning'}`}>{l.availability}</span> : '—'}{l?.lastError && <div className="faint" style={{ fontSize: 12 }}>{l.lastError}</div>}</td></tr>;
                  })}</tbody>
                </table>
                <div className="setup-actions"><button className="btn primary" onClick={next} data-testid="setup-next">Next <ArrowRight size={14} /></button></div>
              </section>
            )}
            {step === 'email' && <EmailStep configured={Boolean(s.email?.configured)} me={me} onNext={next} />}
            {step === 'sso' && s.sso && (
              <section className="setup-card">
                <h2>Single sign-on (optional)</h2>
                <p className="muted">People can sign in with your organisation’s accounts (Microsoft Entra ID, Okta, Google, Keycloak, any OpenID Connect provider). Until you add one, everyone signs in with a PRISM password.</p>
                <Ok ok={s.sso.providers > 0}>{s.sso.providers > 0 ? `${s.sso.providers} identity provider${s.sso.providers === 1 ? '' : 's'} added; mode: ${s.sso.mode}.` : 'No identity provider yet. Add one later under Administration → Access → Single sign-on.'}</Ok>
                <div className="setup-actions">
                  <button className="btn" disabled={busy} onClick={() => void finish('/admin/access?view=sso')} data-testid="setup-open-sso">Finish and set up single sign-on</button>
                  <button className="btn primary" onClick={next} data-testid="setup-next">Next <ArrowRight size={14} /></button>
                </div>
              </section>
            )}
            {step === 'done' && (
              <section className="setup-card">
                <h2>Ready</h2>
                <p className="muted">System status checks the services, workers, storage and security settings, and lists anything that needs attention.</p>
                <div className="setup-actions">
                  <button className="btn primary" disabled={busy} onClick={() => void finish('/admin/status')} data-testid="setup-finish">{busy ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Finish and open the System status checks</button>
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function AccountStep({ me, s, onDone }: { me: Me; s: SetupDTO; onDone: () => void }) {
  const qc = useQueryClient();
  const example = Boolean(s.account?.examplePassword);
  const [f, setF] = useState({ email: s.account?.email ?? me.user.email, displayName: s.account?.displayName ?? me.user.displayName, password: '', again: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (f.password !== f.again) return setError('The passwords are not the same');
    setBusy(true);
    setError(null);
    try {
      await api('/api/v1/setup/account', { method: 'PUT', json: { email: f.email, displayName: f.displayName, newPassword: f.password } });
      await qc.invalidateQueries({ queryKey: queryKeys.me() });
      toast('Administrator account saved');
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  if (!example) {
    return (
      <section className="setup-card">
        <h2>Administrator</h2>
        <Ok ok>Signed in as {me.user.displayName} ({me.user.email}), with a password of your own.</Ok>
        <div className="setup-actions"><button className="btn primary" onClick={onDone} data-testid="setup-next">Next <ArrowRight size={14} /></button></div>
      </section>
    );
  }
  return (
    <form className="setup-card" onSubmit={submit} data-testid="setup-account">
      <h2>Administrator</h2>
      <p className="muted">You signed in with the example password from <code>.env.example</code>, which anyone can look up. Choose your own, and the name and email this account should have.</p>
      {error && <div className="banner error" role="alert">{error}</div>}
      <div className="field-row">
        <div className="field"><label htmlFor="setup-acc-name">Name</label><input id="setup-acc-name" className="input" value={f.displayName} onChange={(e) => setF({ ...f, displayName: e.target.value })} required /></div>
        <div className="field"><label htmlFor="setup-acc-email">Email</label><input id="setup-acc-email" className="input" type="email" autoComplete="username" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required /></div>
      </div>
      <div className="field-row">
        <div className="field"><label htmlFor="setup-acc-pw">New password</label><input id="setup-acc-pw" className="input" type="password" autoComplete="new-password" minLength={10} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required />
          <span className="help">At least 10 characters.</span></div>
        <div className="field"><label htmlFor="setup-acc-pw2">New password again</label><input id="setup-acc-pw2" className="input" type="password" autoComplete="new-password" value={f.again} onChange={(e) => setF({ ...f, again: e.target.value })} required /></div>
      </div>
      <p className="faint" style={{ margin: 0, fontSize: 12 }}>Other sessions of this account are signed out. PRISM_BOOTSTRAP_ADMIN_PASSWORD in .env is only used to create the account the first time.</p>
      <div className="setup-actions"><button className="btn primary" type="submit" disabled={busy || f.password.length < 10} data-testid="setup-save-account">{busy ? <Loader2 size={14} className="spin" /> : <KeyRound size={14} />} Save and continue</button></div>
    </form>
  );
}

function EmailStep({ configured, me, onNext }: { configured: boolean; me: Me; onNext: () => void }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const test = async () => {
    setBusy(true);
    try {
      const r = await api<{ status: string; error?: string }>('/api/v1/system/email/test', { method: 'POST', json: {} });
      setResult(r.status === 'sent' ? `Sent to ${me.user.email}.` : r.status === 'queued' ? 'Queued; it is still being sent.' : `Not sent: ${r.error ?? r.status}`);
    } catch (err) {
      setResult(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="setup-card">
      <h2>Email (optional)</h2>
      <p className="muted">With an SMTP server in <code>.env</code> (PRISM_SMTP_HOST, PRISM_SMTP_FROM and the rest), PRISM mails password reset links and tells people about approvals, tasks and shares. Without it, everything stays in the app.</p>
      <Ok ok={configured}>{configured ? 'Email is set up.' : 'Email is not set up. docs/deployment.md (“Email”) has examples for Microsoft 365, Google Workspace and other SMTP servers.'}</Ok>
      {configured && <button className="btn sm" style={{ alignSelf: 'flex-start' }} disabled={busy} onClick={() => void test()} data-testid="setup-test-email">{busy ? <Loader2 size={13} className="spin" /> : <Mail size={13} />} Send test email</button>}
      {result && <p className="muted" data-testid="setup-email-result" style={{ margin: 0 }}>{result}</p>}
      <div className="setup-actions"><button className="btn primary" onClick={onNext} data-testid="setup-next">Next <ArrowRight size={14} /></button></div>
    </section>
  );
}
